"use client";

import { useEffect, useRef, type ComponentPropsWithoutRef, type ReactNode } from "react";

import { cx } from "../../lib/cx";
import { Icon } from "../core/Icon";
import s from "./Checkbox.module.css";

export interface CheckboxProps extends Omit<ComponentPropsWithoutRef<"input">, "type"> {
  label?: ReactNode;
  /** Segunda línea, más chica y apagada. */
  description?: ReactNode;
  /** "Algunas sí": la raya en lugar de la tilde. Es sólo visual y de accesibilidad; el valor sigue siendo `checked`. */
  indeterminate?: boolean;
}

/**
 * Selección múltiple, o un sí/no que se guarda con el formulario (para un
 * efecto inmediato, `Switch`). El `<input>` real está oculto pero es el que
 * recibe el foco y el teclado; la caja visible se pinta con `:checked`, así
 * que funciona controlado, no controlado y dentro de un `<form>` sin estado.
 */
export function Checkbox({ label, description, indeterminate = false, className, style, disabled, ...rest }: CheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);
  // `indeterminate` no es un atributo HTML: sólo existe como propiedad del elemento.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <label className={cx(s.envoltorio, className)} style={style} data-disabled={disabled ? "" : undefined}>
      <input ref={ref} type="checkbox" className={s.oculto} disabled={disabled} {...rest} />
      <span className={s.caja} aria-hidden="true">
        <span className={s.tilde}>
          <Icon name="check" size={13} strokeWidth={2.4} />
        </span>
        <span className={s.raya} />
      </span>
      {label || description ? (
        <span className={s.texto}>
          {label ? <span className={s.etiqueta}>{label}</span> : null}
          {description ? <span className={s.descripcion}>{description}</span> : null}
        </span>
      ) : null}
    </label>
  );
}
